import { captureException } from '@sentry/react';
import last from 'lodash.last';
import { useEffect, useState } from 'react';
import { v4 as uuid, validate } from 'uuid';

import { queryWithoutParam } from './utils';

export const PROSPECT_ID = 'prospectId';

export const removeProspectIdFromStorage = (storage: Storage) => {
  storage.removeItem(PROSPECT_ID);
};

export const getProspectIdFromUrl = (urlParams: URLSearchParams): string | null => {
  const fullProspectIdFromUrl = urlParams.get(PROSPECT_ID);
  // The link could be like this:
  // https://shop.insify.nl/?prospectId=f9c9fab7-d3f1-4d94-a1de-f8d4b852d370
  // or like this (I couldn't actually find links like that on the landing pages, but they can exist somewhere else):
  // https://shop.insify.nl/?prospectId=frenchCompanyPolicyHolder/f9c9fab7-d3f1-4d94-a1de-f8d4b852d370
  const prospectIdFromUrl = last(fullProspectIdFromUrl?.split('/'));

  if (prospectIdFromUrl) {
    if (validate(prospectIdFromUrl)) {
      return prospectIdFromUrl;
    }
    captureException(new Error(`Received invalid prospect id from url: ${fullProspectIdFromUrl}`));
  }

  return null;
};

export const getFinalProspectId = (
  prospectIdFromUrl: string | null,
  previouslyStoredProspectId: string | null,
): string => {
  if (prospectIdFromUrl) {
    return prospectIdFromUrl;
  }
  const shouldGenerateNewProspectId = !previouslyStoredProspectId || !validate(previouslyStoredProspectId);

  if (shouldGenerateNewProspectId) {
    return uuid();
  }
  return previouslyStoredProspectId;
};

export const sanitizePathname = (path: string) => {
  // Having "//" in the begining of the path confuses history.repaceState function because it can be interpreted as the shorthand of the protocol part in URL
  return path.replace(/^\/{2,}/, '/');
};

export function useInitializeProspect(storage: Storage): string | undefined {
  const [prospectId, setProspectId] = useState<string>();

  // There are multiple ways of how we can initialize a prospect.
  //
  // Notes:
  // session storage is not shared between domains
  // LP — landing pages, APP — funnel app
  // ID — prospect id
  //
  // Flows:
  // 1. The user comes directly to the APP. The user has no ID in the APP sessionStorage.
  //    The ID is generated and stored in the APP sessionStorage.
  // 2. The user comes directly to the APP. The user already has an ID in the APP sessionStorage.
  //    Do nothing.
  // 3. The user comes to the LP. The user may have an ID in the LP sessionStorage, if not then the ID is generated.
  //    The user navigates to the APP. The ID is passed to the APP as a query parameter.
  //      a. ID from the APP sessionStorage !==  ID from query => the ID is stored in the APP sessionStorage.
  //      b. ID from the APP sessionStorage === ID from query => do nothing.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const prospectIdFromUrl = getProspectIdFromUrl(urlParams);

    const previouslyStoredProspectId = storage.getItem(PROSPECT_ID);

    const theFinalProspectId = getFinalProspectId(prospectIdFromUrl, previouslyStoredProspectId);
    storage.setItem(PROSPECT_ID, theFinalProspectId);
    setProspectId(theFinalProspectId);

    const queryWithoutProspectId = queryWithoutParam(urlParams, PROSPECT_ID);
    const safePathname = sanitizePathname(window.location.pathname);
    window.history.replaceState(null, '', `${safePathname}${queryWithoutProspectId}`);
  }, []);

  return prospectId;
}
